import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon, Pill, Tonality } from '@dsny/dsny-component-library'
import { Help } from '@dsny/dsny-component-library/icons'
import styles from '../Dropdowns.module.scss'
import { thunks as ReleasesThunks } from 'reducers/Releases'
import { DropdownTrigger } from './SortDropdown.styles'
import { RELEASE_SORT_OPTIONS } from 'constants/enums'
import { trackEvent } from 'utils/segmentService'
import { TRACKING_EVENTS, SORT_ACTIONS, PLATFORMS } from 'constants/tracking'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'

const SortDropdown = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const selectedSortOption = useSelector((state) => state.releases.sort)

  // Default to 'all_time' from store if nothing is selected
  const currentSort = selectedSortOption
  const currentSortOption = RELEASE_SORT_OPTIONS.find(
    (option) => option.key === currentSort
  )

  const handleSelect = (key) => {
    const sortOption = RELEASE_SORT_OPTIONS.find((opt) => opt.key === key)
    const sortLabel = sortOption?.analyticsLabel || key

    const platformValue = isMobile ? PLATFORMS.MOBILE : PLATFORMS.DESKTOP
    const eventData = {
      action: SORT_ACTIONS.SELECT,
      sort_option: String(key),
      sort_label: String(sortLabel),
      platform: platformValue,
      previous_sort: String(currentSort)
    }

    // Track with Segment (and Statsig via statsigCustom property)
    trackEvent(TRACKING_EVENTS.SORT_INTERACTION, eventData)

    dispatch(ReleasesThunks.sortBy(key))
  }

  return (
    <DropdownMenu.Root>
      <DropdownTrigger>
        <Tonality.Pill
          variant="tonal"
          mode="dark"
          label={
            currentSortOption
              ? t(currentSortOption.translationKey)
              : t('home.sort-all-time')
          }
          iconPosition="tailing"
          icon={
            <MaterialIcon
              name="KeyboardArrowDown"
              color={theme.colors.white0}
              style={{ fontSize: 14 }}
            />
          }
        />
      </DropdownTrigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="bottom"
          align="end"
          style={{
            width: 296,
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100,
            padding: '5px 0',
            height: 'auto',
            maxHeight: 216,
            overflow: 'auto',
            border: `0.5px solid ${theme.colors.neutralW30}`
          }}
          className={`${styles.DropdownMenuContent} , ${styles.MenuContent}`}
          sideOffset={5}
          avoidCollisions
          collisionPadding={20}
        >
          {RELEASE_SORT_OPTIONS.map((opt) => (
            <DropdownMenu.Item
              key={opt.key}
              className={styles.DropdownMenuItem}
              style={{
                cursor: 'pointer',
                padding: '8px 16px',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                transition: 'background 0.2s, color 0.2s'
              }}
              onSelect={() => handleSelect(opt.key)}
            >
              <Tonality.UIText textStyle="body" style={{ fontSize: 14 }}>
                {t(opt.translationKey)}
              </Tonality.UIText>
              {opt.info && (
                <Tonality.Tooltip
                  mode="dark"
                  content={t('home.trending-sort-tooltip')}
                  containerStyles={{ padding: '4px 8px', maxWidth: 290 }}
                >
                  <div>
                    <Help
                      color={theme.colors.neutralW100}
                      style={{ marginLeft: 8, fontSize: 18 }}
                      width={16}
                      height={16}
                    />
                  </div>
                </Tonality.Tooltip>
              )}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default SortDropdown
